import { Swiper, SwiperSlide } from 'swiper/react'
import {
    LuxPhotoCard,
    LuxPhotoFullScreen,
    LuxButton,
    Color,
    LuxIconSpinner,
} from '@lux/wardrobe-lib'
import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import DialogUploadPhoto from './DialogUploadPhoto'
import DialogPhotoDetailsUpload from './DialogPhotoDetailsUpload'
import { statusIconMap } from '@/app/models/photo.status'
import DialogConfirmDeletePhoto from './DialogConfirmDeletePhoto'
import { connect } from 'react-redux'
import { newActivityCreationError } from '@/app/redux/actions/activity-creation'
import { NEW_ACTIVITY_EXECUTION_NAMESPACE } from '@/app/redux/reducers/activity-execution'
import {
    deleteActivityTaskPhoto,
    getActivityTaskPhoto,
    reloadProjectStatus,
    resetActivityTaskPhoto,
    uploadActivityTaskPhoto,
} from '@/app/redux/actions/activity-execution'
import { PhotoGallery } from '@/app/models/photo'
import { layoutActions, ModalsType } from '@Techedge/digitalportal-shared'
import { TaskPhotoUpload } from '@/app/models/projects-service'
import { usePhotoFullScreen } from '@/app/hooks/usePhotoFullScreen'
import { useUploadPhoto } from '@/app/hooks/useUploadPhoto'
import './Photo.scss'
import 'swiper/css'

export interface PhotoProps {
    numberOfPhotos: number
    projectTitle: string
    projectType: string
    activityPhotoList: PhotoGallery[]
    projectId: string
    storeId: string
    canUpload: boolean
    totalActivityPhotoList: number
    loadingActivityPhotoList: boolean
    modalLoading: boolean
    resetActivityTaskPhoto: () => void
    openSavePictureModal: (id: string, executionProjectId: string) => void
    deleteActivityTaskPhoto: (attachmentsIds: string[], callback: () => void) => void
    reloadProjectStatus: (projectId: string, stores?: string[]) => void
    setModalLoading: (loading: boolean) => void
    uploadActivityTaskPhoto: (photos: TaskPhotoUpload[], callback: () => void) => void
    newActivityCreationError: (err: string) => void
    getActivityTaskPhoto: (projectId: string, storeId: string, params?: any) => void
}

const Photo = ({
    numberOfPhotos,
    projectTitle,
    projectType,
    activityPhotoList,
    projectId,
    storeId,
    canUpload,
    totalActivityPhotoList,
    loadingActivityPhotoList,
    modalLoading,
    newActivityCreationError,
    getActivityTaskPhoto,
    resetActivityTaskPhoto,
    openSavePictureModal,
    deleteActivityTaskPhoto,
    reloadProjectStatus,
    setModalLoading,
    uploadActivityTaskPhoto,
}: PhotoProps) => {
    const componentClass = 'smart-modals-photo-execution'
    const intl = useIntl()

    const [dialogPhotoDetailsVisible, setDialogPhotoDetailsVisible] = useState<boolean>(false)
    const [currentPage, setCurrentPage] = useState<number>(1)
    const [dialogConfirmDeletePhotoVisible, setDialogConfirmDeletePhotoVisible] =
        useState<boolean>(false)
    const [deletePhotoId, setDeletePhotoId] = useState<string>(null)

    const {
        editImageExt,
        editImageId,
        imageList,
        setImageList,
        uploadedImageList,
        setUploadedImageList,
        selectedImage,
        setSelectedImage,
        selectedImageInfo,
        enabledEdit,
        tagList,
        setTagList,
        newTagPosition,
        tagOnImageList,
        onAddTag,
        onRemoveTag,
        onNewTagPosition,
        onCloseFullScreen,
        previousImage,
        nextImage,
        slide,
        setSlide,
        editLoading,
        setEditLoading,
        onExpandImage,
        onTag,
        onEdit,
    } = usePhotoFullScreen({})

    const onFormatError = () => {
        newActivityCreationError(
            intl.formatMessage({
                id: 'modals.new.activity.section.main.info.project.cover.format.error',
                defaultMessage:
                    'Selected image format cannot be processed, please use images with one of the following formats: .apng,.avif,.gif,.jpg,.jpeg,.jfif,.pjpeg,.pjp,.png,.svg,.webp, .bmp,.ico,.cur,.tif,.tiff',
            })
        )
    }

    const onSizeError = () => {
        newActivityCreationError(
            intl.formatMessage({
                id: 'modals.new.activity.section.main.info.project.cover.size.error',
                defaultMessage: 'Selected image exceeds the size limit of 20MB',
            })
        )
    }

    const {
        imagePosition,
        setImagePosition,
        dialogUploadPhotoVisible,
        setDialogUploadPhotoVisible,
        onFileChange,
        uploadImageList,
        onEditImage,
    } = useUploadPhoto({
        uploadedImageList,
        setUploadedImageList,
        setModalLoading,
        onFormatError,
        onSizeError,
        onUploadDone: () => setDialogPhotoDetailsVisible(true),
        setSelectedImage,
    })

    const onEditDone = (blob: Blob) => {
        const file = new File([blob], selectedImageInfo.title)
        const reader: any = new FileReader()
        reader.onloadend = async () => {
            setImagePosition(slide + 1)
            uploadImageList({
                files: [
                    {
                        url: reader.result.toString(),
                        name: file.name,
                        fileObj: file,
                    },
                ],
                editImagePosition: slide + 1,
            })
            setSelectedImage(reader.result.toString())
        }
        if (!editImageId) {
            reader.readAsDataURL(blob)
        } else {
            onEditImage(blob, `${selectedImageInfo.title}.${editImageExt}`, editImageId, slide + 1)
            resetActivityTaskPhoto()
        }
    }

    const onDeletePhoto = (photoId: string) => {
        setDeletePhotoId(photoId)
        setDialogConfirmDeletePhotoVisible(true)
    }

    const onConfirmDelete = () => {
        deleteActivityTaskPhoto([deletePhotoId], () => {
            resetActivityTaskPhoto()
            getActivityTaskPhoto(projectId, storeId)
        })
        setDialogConfirmDeletePhotoVisible(false)
    }

    const onDeleteUploadedPhoto = (photoName: string) => {
        setUploadedImageList(uploadedImageList.filter((i) => i.name !== photoName))
    }

    const onClosePhotoDetails = () => {
        setDialogPhotoDetailsVisible(false)
        setUploadedImageList([])
        setSlide(0)
    }

    const onSaveImage = (
        macroareaMap: Record<string, string>,
        fixtureCountMap: Record<string, number>
    ) => {
        uploadActivityTaskPhoto(
            uploadedImageList.map((image) => ({
                projectId,
                storeId,
                attachment: { id: image.attachmentId },
                macroarea: macroareaMap[image.attachmentId],
                fixtureCount: fixtureCountMap[image.attachmentId],
            })),
            () => {
                setDialogPhotoDetailsVisible(false)
                setUploadedImageList([])
                resetActivityTaskPhoto()
                getActivityTaskPhoto(projectId, storeId)
                reloadProjectStatus(projectId, [storeId])
            }
        )
    }

    const nextPage = () => {
        setCurrentPage((page) => page + 1)
    }

    const onSwiperActiveIndexChange = (swiper) => {
        const index = swiper.activeIndex + 1
        if (index === activityPhotoList.length && index < totalActivityPhotoList) nextPage()
    }

    useEffect(() => {
        reloadProjectStatus(projectId, [storeId])
        resetActivityTaskPhoto()
    }, [])

    useEffect(() => {
        setTagList([
            {
                label: 'Accessories',
                command: onAddTag.bind(null, 'Accessories'),
            },
            {
                label: 'Backstock',
                command: onAddTag.bind(null, 'Backstock'),
            },
        ])
    }, [newTagPosition])

    useEffect(() => {
        setImageList(
            [...activityPhotoList].map((photo: PhotoGallery) => ({
                photo: {
                    url: photo.url,
                    name: photo.name,
                    id: photo._id,
                },
                actions: canUpload
                    ? [
                          {
                              label: 'pencil',
                              icon: 'pencil',
                              command: () => {
                                  setUploadedImageList([photo])
                                  setDialogPhotoDetailsVisible(true)
                              },
                          },
                          {
                              label: 'delete',
                              icon: 'trash',
                              command: () => onDeletePhoto(photo._id),
                          },
                      ]
                    : [],
                expandEnabled: true,
                statusIconTooltip: intl.formatMessage({
                    id: `gallery.detail.task_photo.status.${photo.status}`,
                    defaultMessage: photo.status,
                }),
                statusIcon: statusIconMap[photo.status]?.icon,
                statusIconColor: statusIconMap[photo.status]?.color,
                onExpand: () => {
                    onExpandImage({
                        url: photo.url,
                        name: photo.name,
                        id: photo._id,
                        ext: photo.ext,
                    })
                },
            }))
        )
    }, [activityPhotoList])

    useEffect(() => {
        getActivityTaskPhoto(projectId, storeId, { page: currentPage, pageSize: 10 })
    }, [currentPage])

    return (
        <div className={componentClass}>
            <div className={`${componentClass}__num-photo text`}>
                {numberOfPhotos > 0 &&
                    `${intl.formatMessage({
                        id: 'modals.task.photo.action.number.photos',
                        defaultMessage: 'Minimun photos',
                    })}: ${numberOfPhotos}`}
                {!numberOfPhotos &&
                    intl.formatMessage({
                        id: 'modals.task.photo.action.number.photos.zero',
                        defaultMessage: 'No minimun photos',
                    })}
            </div>
            <div className={`${componentClass}__content`}>
                <div className={`${componentClass}__content--mobile`}>
                    <Swiper
                        spaceBetween={15}
                        slidesPerView="auto"
                        onSlideChange={onSwiperActiveIndexChange}>
                        {canUpload && (
                            <SwiperSlide key="addPhoto">
                                <LuxPhotoCard
                                    photo={{ url: '' }}
                                    actions={[
                                        {
                                            label: intl.formatMessage({
                                                id: 'ph2.modals.photo.slider.add.photo',
                                                defaultMessage: 'Add Photo',
                                            }),
                                            icon: '',
                                            command: () => setDialogUploadPhotoVisible(true),
                                        },
                                    ]}
                                    key="addPhoto"
                                />
                            </SwiperSlide>
                        )}
                        {imageList.map((image, i) => {
                            return (
                                <SwiperSlide key={i}>
                                    <div key={i} className={`${componentClass}__card`}>
                                        <LuxPhotoCard {...image} key={i} />
                                    </div>
                                </SwiperSlide>
                            )
                        })}
                    </Swiper>
                </div>
                <div className={`${componentClass}__content--web`}>
                    {canUpload && (
                        <LuxPhotoCard
                            photo={{ url: '' }}
                            actions={[
                                {
                                    label: intl.formatMessage({
                                        id: 'ph2.modals.photo.slider.add.photo',
                                        defaultMessage: 'Add Photo',
                                    }),
                                    icon: '',
                                    command: () => setDialogUploadPhotoVisible(true),
                                },
                            ]}
                            key="addPhoto"
                        />
                    )}
                    {imageList.map((image, i) => {
                        return (
                            <div key={i} className={`${componentClass}__card`}>
                                <LuxPhotoCard {...image} key={i} />
                            </div>
                        )
                    })}
                    {loadingActivityPhotoList &&
                        totalActivityPhotoList > activityPhotoList.length && <LuxIconSpinner />}
                    {!loadingActivityPhotoList &&
                        totalActivityPhotoList > activityPhotoList.length && (
                            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                                <LuxButton
                                    color={Color.BLUE}
                                    variant="text"
                                    text={intl.formatMessage({
                                        id: 'gallery.detail.load.more.button',
                                        defaultMessage: 'Load more photo',
                                    })}
                                    onClick={nextPage}
                                />
                            </div>
                        )}
                </div>
            </div>
            <DialogUploadPhoto
                visible={dialogUploadPhotoVisible}
                multiple={!imagePosition}
                modalLoading={modalLoading}
                projectName={projectTitle}
                onFileChange={onFileChange}
                onHide={() => setDialogUploadPhotoVisible(false)}
            />
            <DialogConfirmDeletePhoto
                visible={dialogConfirmDeletePhotoVisible}
                onHide={() => setDialogConfirmDeletePhotoVisible(false)}
                onConfirm={onConfirmDelete}
            />
            {dialogPhotoDetailsVisible && (
                <DialogPhotoDetailsUpload
                    onClose={onClosePhotoDetails}
                    onExpandImage={onExpandImage}
                    onRetakePhoto={(imagePosition) => {
                        setImagePosition(imagePosition)
                        setDialogUploadPhotoVisible(true)
                    }}
                    onAddPhoto={() => setDialogUploadPhotoVisible(true)}
                    onSave={onSaveImage}
                    onDeleteUploadedPhoto={onDeleteUploadedPhoto}
                    onTag={onTag}
                    onEdit={onEdit}
                    storeId={storeId}
                    projectType={projectType}
                    projectName={projectTitle}
                    uploadedImageList={uploadedImageList}
                    slide={slide}
                    setSlide={setSlide}
                />
            )}

            {selectedImage && (
                <LuxPhotoFullScreen
                    selectedImage={selectedImage}
                    tagList={tagList}
                    tagOnImageList={tagOnImageList}
                    newTagPosition={newTagPosition}
                    imageInfo={selectedImageInfo}
                    enabledEdit={enabledEdit}
                    totalImage={uploadedImageList.length || imageList.length}
                    editLoading={editLoading}
                    intl={intl}
                    disableInfo={uploadedImageList.length > 0 && !uploadedImageList[slide]?.id}
                    setEditLoading={setEditLoading}
                    onNewTagPosition={onNewTagPosition}
                    onClose={() => {
                        resetActivityTaskPhoto()
                        getActivityTaskPhoto(projectId, storeId, {
                            page: 1,
                            pageSize: 10,
                        })
                        onCloseFullScreen()
                    }}
                    onRemoveTag={onRemoveTag}
                    onEditDone={onEditDone}
                    previousImage={previousImage}
                    nextImage={nextImage}
                    onOpenInfo={() => {
                        openSavePictureModal(editImageId, projectId)
                        onCloseFullScreen()
                    }}
                    disableTag
                />
            )}
        </div>
    )
}

const mapStateToProps = (state: any) => ({
    activityPhotoList: state[NEW_ACTIVITY_EXECUTION_NAMESPACE].activityPhotoList,
    totalActivityPhotoList: state[NEW_ACTIVITY_EXECUTION_NAMESPACE].totalActivityPhotoList,
    loadingActivityPhotoList: state[NEW_ACTIVITY_EXECUTION_NAMESPACE].loadingActivityPhotoList,
    projectId: state.layoutReducer.modals.props?.id,
    modalLoading: state.layoutReducer.modals.props?.loading,
})

const mapDispatchToProps = (dispatch: any) => ({
    newActivityCreationError: (err: string) => dispatch(newActivityCreationError(err)),
    getActivityTaskPhoto: (projectId: string, storeId: string, params?: any) =>
        dispatch(getActivityTaskPhoto(projectId, storeId, params)),
    uploadActivityTaskPhoto: (photos: TaskPhotoUpload[], callback: () => void) =>
        dispatch(uploadActivityTaskPhoto(photos, callback)),
    deleteActivityTaskPhoto: (attachmentsIds: string[], callback: () => void) =>
        dispatch(deleteActivityTaskPhoto(attachmentsIds, callback)),
    setModalLoading: (loading: boolean) =>
        dispatch(layoutActions.setModals({ props: { loading } })),
    openSavePictureModal: (id: string, executionProjectId: string) =>
        dispatch(
            layoutActions.setModals({
                type: ModalsType.SAVE_PICTURE,
                props: {
                    id,
                    executionProjectId,
                },
            })
        ),
    resetActivityTaskPhoto: () => dispatch(resetActivityTaskPhoto()),
    reloadProjectStatus: (projectId: string, stores?: string[]) =>
        dispatch(reloadProjectStatus(projectId, stores)),
})

export default connect(mapStateToProps, mapDispatchToProps)(Photo)
